import {
  currentPasswordSchema,
  mfaCodeSchema,
  type CurrentUser,
  type ReauthenticateRequest,
} from '@mfos/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type SubmitEvent, type ReactNode, useEffect, useState } from 'react';
import { Dialog } from '../../components/Dialog';
import { ErrorNotice } from '../../components/ErrorNotice';
import { TextField } from '../../components/Field';
import { setReauthenticationHandler } from '../../lib/api-client';
import { authKeys, reauthenticate } from '../auth/auth.api';
import { phrase } from '../../lib/i18n/runtime';

type ResolveReauth = (confirmed: boolean) => void;

/**
 * Step-up authentication for sensitive actions. When a mutation answers
 * `REAUTHENTICATION_REQUIRED`, the API client awaits this dialog and retries the mutation once
 * after a successful `POST /auth/reauthenticate`; cancelling fails the mutation.
 */
export function ReauthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [resolve, setResolve] = useState<ResolveReauth | null>(null);

  useEffect(() => {
    setReauthenticationHandler(
      () =>
        new Promise<boolean>((resolvePromise) => {
          setResolve(() => resolvePromise);
        }),
    );
    return () => {
      setReauthenticationHandler(null);
    };
  }, []);

  function finish(confirmed: boolean) {
    resolve?.(confirmed);
    setResolve(null);
  }

  const user = queryClient.getQueryData<CurrentUser>(authKeys.me);

  return (
    <>
      {children}
      <Dialog
        open={resolve !== null}
        title={phrase('Confirm it’s you')}
        onClose={() => {
          finish(false);
        }}
        description="This is a sensitive action. Confirm your identity to continue; you will not be asked again for about 10 minutes."
      >
        <ReauthForm
          user={user}
          onConfirmed={(updated) => {
            queryClient.setQueryData(authKeys.me, updated);
            finish(true);
          }}
          onCancel={() => {
            finish(false);
          }}
        />
      </Dialog>
    </>
  );
}

type Method = 'password' | 'code';

function ReauthForm({
  user,
  onConfirmed,
  onCancel,
}: {
  user: CurrentUser | undefined;
  onConfirmed: (user: CurrentUser) => void;
  onCancel: () => void;
}) {
  const passwordAvailable = user?.hasPassword ?? true;
  const codeAvailable = user?.mfaEnabled ?? true;
  const [method, setMethod] = useState<Method>(passwordAvailable ? 'password' : 'code');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>();
  const mutation = useMutation({
    mutationFn: (body: ReauthenticateRequest) => reauthenticate(body),
    onSuccess: onConfirmed,
  });

  function submit(event: SubmitEvent) {
    event.preventDefault();
    if (method === 'password') {
      const parsed = currentPasswordSchema.safeParse(password);
      if (!parsed.success) {
        setFieldError('Enter your password.');
        return;
      }
      setFieldError(undefined);
      mutation.mutate({ password: parsed.data });
    } else {
      const parsed = mfaCodeSchema.safeParse(code);
      if (!parsed.success) {
        setFieldError('Enter the 6-digit code from your authenticator app or a recovery code.');
        return;
      }
      setFieldError(undefined);
      mutation.mutate({ code: parsed.data });
    }
  }

  if (!passwordAvailable && !codeAvailable) {
    return (
      <>
        <p>
          {phrase(
            'Your account has no password or authenticator app. Sign out and sign in again with a magic link, then retry the action within 10 minutes.',
          )}
        </p>
        <div className="dialog__actions">
          <button type="button" className="button button--secondary" onClick={onCancel}>
            {phrase('Close')}
          </button>
        </div>
      </>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      {passwordAvailable && codeAvailable && (
        <fieldset className="choice-group">
          <legend>{phrase('Confirm with')}</legend>
          <label>
            <input
              type="radio"
              name="reauth-method"
              checked={method === 'password'}
              onChange={() => {
                setMethod('password');
                setFieldError(undefined);
              }}
            />{' '}
            Password
          </label>
          <label>
            <input
              type="radio"
              name="reauth-method"
              checked={method === 'code'}
              onChange={() => {
                setMethod('code');
                setFieldError(undefined);
              }}
            />{' '}
            Authentication code
          </label>
        </fieldset>
      )}
      {method === 'password' ? (
        <TextField
          label={phrase('Password')}
          type="password"
          autoComplete="current-password"
          value={password}
          onValueChange={setPassword}
          error={fieldError}
          required
        />
      ) : (
        <TextField
          label={phrase('Authentication code')}
          autoComplete="one-time-code"
          value={code}
          onValueChange={setCode}
          error={fieldError}
          hint={phrase('6-digit code from your authenticator app, or a recovery code.')}
          required
        />
      )}
      <ErrorNotice error={mutation.error} />
      <div className="dialog__actions">
        <button
          type="button"
          className="button button--secondary"
          onClick={onCancel}
          disabled={mutation.isPending}
        >
          {phrase('Cancel')}
        </button>
        <button type="submit" className="button" disabled={mutation.isPending}>
          {mutation.isPending ? phrase('Confirming…') : phrase('Confirm')}
        </button>
      </div>
    </form>
  );
}
